import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssignPipelinesToGroup } from '../services/queries';
import apiClient from '../services/apiClient';
import { PipelineDto } from '../types/pipelineFlow';

interface SelectedPipeline {
  pipelineId: string;
}

const PipelineSelection: React.FC = () => {
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const createMultiplePipelines = useAssignPipelinesToGroup();
  
  const [pipelines, setPipelines] = useState<PipelineDto[]>([]);
  const [selectedPipelines, setSelectedPipelines] = useState<SelectedPipeline[]>([]);
  const [filteredPipelines, setFilteredPipelines] = useState<PipelineDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPipelines();
  }, []);

  useEffect(() => {
    // Filter pipelines based on search term
    let filtered = pipelines;

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(pipeline =>
        pipeline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pipeline.pipelineName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPipelines(filtered);
  }, [pipelines, searchTerm]);

  const fetchPipelines = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPipelines();
      setPipelines(data);
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to load pipelines. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePipelineSelection = (pipeline: PipelineDto) => {
    const isSelected = selectedPipelines.some(p => p.pipelineId === pipeline.id);
    
    if (isSelected) {
      setSelectedPipelines(prev => prev.filter(p => p.pipelineId !== pipeline.id));
    } else {
      setSelectedPipelines(prev => [...prev, {
        pipelineId: pipeline.id
      }]);
    }
  };

  const handleSave = async () => {
    if (selectedPipelines.length === 0) {
      setError('Please select at least one pipeline.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const requestBody = {
        deploymentGroupId: groupId!,
        pipelineIds: selectedPipelines.map(p => p.pipelineId)
      };

      await createMultiplePipelines.mutateAsync(requestBody);
      
      // Navigate back to the deployment group
      navigate(`/admin/deployment-groups/${groupId}`);
    } catch (err) {
      console.error('Error creating pipelines:', err);
      setError(err instanceof Error ? err.message : 'Failed to create pipelines. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isPipelineSelected = (pipelineId: string) => {
    return selectedPipelines.some(p => p.pipelineId === pipelineId);
  };

  const hasActiveFilters = !!searchTerm;

  const clearAllFilters = () => {
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Select Pipelines</h1>
          <p className="text-gray-600 mt-2">
            Choose pipelines to add as pipelines to this group
          </p>
        </div>
        <button
          onClick={() => navigate(`/admin/deployment-groups/${groupId}`)}
          className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {/* Search Bar */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search pipelines by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="flex items-center gap-4">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 rounded-lg border bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Clear Search
              </button>
            )}
            <span className="text-sm text-gray-600">
              {selectedPipelines.length} selected
            </span>
          </div>
        </div>
      </div>

      {/* Pipeline List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Available Pipelines
            </h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>
                Showing {filteredPipelines.length} of {pipelines.length} pipelines
              </span>
              {hasActiveFilters && (
                <span className="text-blue-600 font-medium">
                  (filtered)
                </span>
              )}
            </div>
          </div>
        </div>
        
        {filteredPipelines.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="mb-4">
              {hasActiveFilters ? (
                <>
                  <p className="text-lg mb-2">No pipelines match your current filters</p>
                  <p className="text-sm">Try adjusting or clearing your filters to see more results.</p>
                  <button
                    onClick={clearAllFilters}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Clear All Filters
                  </button>
                </>
              ) : (
                'No pipelines available.'
              )}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  isPipelineSelected(pipeline.id) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={isPipelineSelected(pipeline.id)}
                      onChange={() => togglePipelineSelection(pipeline)}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {pipeline.name}
                      </h3>
                      <div className="text-sm text-gray-500 space-y-1">
                        <div>{pipeline.pipelineName} • Definition #{pipeline.definitionId}</div>
                        {pipeline.deploymentGroupName && (
                          <div>Group: {pipeline.deploymentGroupName}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <div>ID: {pipeline.id}</div>
                    <div>Modified: {new Date(pipeline.lastModifiedOn).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPipelines.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
          <div className="w-full flex items-center justify-between">
            <span className="text-gray-600">
              {selectedPipelines.length} pipeline{selectedPipelines.length !== 1 ? 's' : ''} selected
            </span>
            <div className="space-x-2">
              <button
                onClick={() => setSelectedPipelines([])}
                className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                Clear Selection
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-75"
              >
                {saving ? 'Saving...' : `Add ${selectedPipelines.length} Pipeline${selectedPipelines.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineSelection;